<template>
  <div class="promotions-form">
    <a-spin :spinning="loading" :tip="labels.form.loading">
      <ValidationObserver ref="observer">
        <a-form
          :model="form"
          :label-width="80"
          slot-scope="{ validate }"
          @submit.prevent="validate().then(handleSubmit)"
        >
          <a-row
            type="flex"
            justify="space-between"
            :gutter="16"
            :style="{ marginBottom: '15px' }"
          >
            <!-- Title -->
            <a-col :xs="24" :md="12">
              <h2 class="form-title">
                <a-icon :type="isEdit ? 'edit' : 'form'" class="form-icon" />
                <span>{{
                  isEdit
                    ? `${labels.form.titleView} número: ${form.code || ""}`
                    : labels.form.titleProcess
                }}</span>
              </h2>
            </a-col>
            <!-- Actions -->
            <a-col :xs="24" :md="12">
              <a-row
                type="flex"
                justify="end"
                :gutter="16"
                :style="{
                  alignItems: 'flex-end',
                  marginBottom: '0px',
                }"
              >
                <a-col>
                  <a-button-group>
                    <a-button
                      :type="isEdit ? 'primary' : 'danger'"
                      ghost
                      @click="handleCancel"
                      icon="arrow-left"
                    >
                      <span>{{ labels.form.goBack }}</span>
                    </a-button>
                    <a-button
                      v-if="isEdit && canDelete"
                      type="danger"
                      @click="confirmDelete"
                    >
                      <span>{{ labels.form.deletePromotion }}</span>
                    </a-button>
                    <a-button
                      v-if="!isMobile && canEdit"
                      type="primary"
                      ghost
                      :loading="submitting"
                      @click="validate().then(() => handleSubmit(true))"
                    >
                      <span>{{ labels.form.saveAndClose }}</span>
                    </a-button>
                    <a-button
                      v-if="canEdit"
                      type="primary"
                      :loading="submitting"
                      @click="validate().then(handleSubmit)"
                    >
                      <span>{{ labels.form.save }}</span>
                    </a-button>
                    <a-dropdown :trigger="['click']" v-if="isMobile && canEdit">
                      <a-menu slot="overlay">
                        <a-menu-item
                          key="1"
                          @click="validate().then(() => handleSubmit(true))"
                          >{{ labels.form.saveAndClose }}</a-menu-item
                        >
                      </a-menu>
                      <a-button type="primary" ghost icon="more" />
                    </a-dropdown>
                  </a-button-group>
                </a-col>
              </a-row>
            </a-col>
          </a-row>
          <a-row
            type="flex"
            justify="space-between"
            :gutter="12"
            :style="{ marginBottom: '15px' }"
          >
            <a-col :span="24">
              <a-card>
                <a-row type="flex" justify="space-between" :gutter="12">
                  <a-col :xs="24" :sm="24" :md="6">
                    <Input
                      v-model="form.code"
                      :vid="labels.form.code.label"
                      :label="labels.form.code.label"
                      :name="labels.form.code.label"
                      :formitem="{ label: labels.form.code.label }"
                      :placeholder="labels.form.code.placeholder"
                      @input="onlyAlfaNumeric($event, 'code')"
                      rules="required"
                      :readOnly="!canEdit"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="6">
                    <SwitchInput
                      v-model="form.status"
                      :label="labels.form.status.label"
                      :disabled="!canEdit"
                    />
                  </a-col>
                </a-row>
                <a-row type="flex" justify="space-between" :gutter="12">
                  <a-col :xs="24" :sm="24" :md="8">
                    <SelectValidation
                      v-model="form.type"
                      :placeholder="labels.form.type.placeholder"
                      :label="labels.form.type.label"
                      :dataSource="promotionTypes"
                      :allowClear="true"
                      rules="required"
                      :disabled="!canEdit"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <Input
                      v-model="form.quantity"
                      type="number"
                      :vid="labels.form.quantity.label"
                      :label="labels.form.quantity.label"
                      :name="labels.form.quantity.label"
                      :formitem="{ label: labels.form.quantity.label }"
                      :placeholder="labels.form.quantity.placeholder"
                      :min="1"
                      v-bind="isPercentage ? { max: 100 } : {}"
                      rules="required"
                      :readOnly="!canEdit"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <SelectValidation
                      v-model="form.use_type"
                      :placeholder="labels.form.useType.placeholder"
                      :label="labels.form.useType.label"
                      :dataSource="useTypes"
                      :allowClear="true"
                      rules="required"
                      :disabled="!canEdit"
                    />
                  </a-col>
                </a-row>

                <a-row type="flex" justify="space-between" :gutter="12">
                  <a-col :xs="24" :sm="24" :md="8">
                    <Input
                      v-model="form.uses"
                      type="number"
                      :vid="labels.form.uses.label"
                      :label="labels.form.uses.label"
                      :name="labels.form.uses.label"
                      :formitem="{ label: labels.form.uses.label }"
                      :placeholder="labels.form.uses.placeholder"
                      :min="1"
                      rules="required"
                      :readOnly="!canEdit"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <DatePicker
                      v-model="form.start_use_at"
                      :vid="labels.form.startAt.label"
                      :name="labels.form.startAt.label"
                      :formitem="{ label: labels.form.startAt.label }"
                      :placeholder="labels.form.startAt.placeholder"
                      :format="dateFormat"
                      :disabled="!canEdit"
                    />
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="8">
                    <DatePicker
                      v-model="form.expire_at"
                      :vid="labels.form.expireAt.label"
                      :name="labels.form.expireAt.label"
                      :formitem="{ label: labels.form.expireAt.label }"
                      :placeholder="labels.form.expireAt.placeholder"
                      :format="dateFormat"
                      :disabled="!canEdit"
                    />
                  </a-col>
                </a-row>

                <a-divider orientation="left" :style="{ marginTop: '36px' }">{{
                  labels.form.validations
                }}</a-divider>

                <!-- VALIDATIONS -->
                <a-row
                  type="flex"
                  justify="start"
                  :gutter="12"
                  :style="{ marginTop: '15px' }"
                >
                  <a-col :span="24">
                    <CheckboxInput
                      v-model="form.oatur"
                      :label="labels.form.oatur.label"
                      :disabled="!canEdit"
                    />
                  </a-col>
                  <a-col v-if="form.oatur" :span="24">
                    <SelectPagination
                      v-model="form.oatur_values"
                      :fetchService="fetchUsers"
                      :placeholder="labels.form.oaturValues.placeholder"
                      :label="labels.form.oaturValues.label"
                      :formItem="{ colon: false }"
                      :labelKey="'fullname'"
                      :valueKey="'id'"
                      :disabledDefaultFetchOptions="false"
                      :allowClear="true"
                      :showLabel="false"
                      mode="multiple"
                      :disabled="!canEdit"
                      rules="required"
                    >
                      <template #renderOption="{ data }">
                        <span>{{ data.fullname }} ({{ data.email }})</span>
                      </template>
                    </SelectPagination>
                  </a-col>
                </a-row>

                <a-divider :style="{ margin: '12px 0' }" />
                <a-row
                  type="flex"
                  justify="start"
                  :gutter="12"
                  :style="{ marginTop: '15px' }"
                >
                  <a-col :span="24">
                    <CheckboxInput
                      v-model="form.oaturwnda"
                      :label="labels.form.oaturwnda.label"
                      :disabled="!canEdit"
                    />
                  </a-col>
                  <a-col v-if="form.oaturwnda" :xs="24" :sm="12" :md="6">
                    <Input
                      v-model="form.oaturwnda_value"
                      type="number"
                      :vid="labels.form.oaturwndaValue.label"
                      :label="labels.form.oaturwndaValue.label"
                      :name="labels.form.oaturwndaValue.label"
                      :formitem="{
                        label: labels.form.oaturwndaValue.label,
                        colon: false,
                      }"
                      :placeholder="labels.form.oaturwndaValue.placeholder"
                      :showLabel="false"
                      :min="1"
                      rules="required|min:1"
                      :readOnly="!canEdit"
                    />
                  </a-col>
                </a-row>

                <a-divider :style="{ margin: '12px 0' }" />
                <a-row
                  type="flex"
                  justify="start"
                  :gutter="12"
                  :style="{ marginTop: '15px' }"
                >
                  <a-col :span="24">
                    <CheckboxInput
                      v-model="form.oatuwditlqod"
                      :label="labels.form.oatuwditlqod.label"
                      :disabled="!canEdit"
                    />
                  </a-col>
                  <a-col v-if="form.oatuwditlqod" :xs="24" :sm="12" :md="6">
                    <Input
                      v-model="form.oatuwditlqod_value"
                      type="number"
                      :vid="labels.form.oatuwditlqodValue.label"
                      :label="labels.form.oatuwditlqodValue.label"
                      :name="labels.form.oatuwditlqodValue.label"
                      :formitem="{
                        label: labels.form.oatuwditlqodValue.label,
                        colon: false,
                      }"
                      :placeholder="labels.form.oatuwditlqodValue.placeholder"
                      :showLabel="false"
                      :min="1"
                      rules="required|min:1"
                      :readOnly="!canEdit"
                    />
                  </a-col>
                </a-row>
              </a-card>
            </a-col>
          </a-row>
        </a-form>
      </ValidationObserver>
    </a-spin>
  </div>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import labels from "@/utils/labels";
import Input from "@/components/core/VeeValidateForm/Input.vue";
import DatePicker from "@/components/core/VeeValidateForm/DatePicker.vue";
import SelectValidation from "@/components/core/VeeValidateForm/Select.vue";
import SelectPagination from "@/components/core/SelectPagination/index.vue";
import SwitchInput from "@/components/core/VeeValidateForm/Switch.vue";
import CheckboxInput from "@/components/core/VeeValidateForm/Checkbox.vue";
import { mapActions, mapGetters, mapState } from "vuex";
import _ from "lodash";
import moment from "moment";
import router from "@/router";
import * as actions from "@/utils/actions";
import { mask } from "vue-the-mask";
import { adminPageTitle, hasRoles, roles } from "../../../utils/utils";

const initialFormState = {
  code: "",
  type: "percentage_next_deposit",
  use_type: "custom_quantity",
  start_use_at: moment().startOf("day"),
  expire_at: undefined,
  status: true,
  uses: 1,
  description: "",
  oatur: false,
  oatur_values: [],
  oaturwnda: false,
  oaturwnda_value: 0,
  oatuwditlqod: false,
  oatuwditlqod_value: 0,
};

export default {
  name: "PromotionForm",
  directives: { themask: mask },
  components: {
    ValidationObserver,
    Input,
    DatePicker,
    SelectValidation,
    SwitchInput,
    CheckboxInput,
    SelectPagination,
  },
  metaInfo: {
    title: adminPageTitle(labels.promotions.title),
    titleTemplate: () => {
      return `${adminPageTitle()} - ${
        router.currentRoute.name.includes("view")
          ? labels.promotions.pageTitleViewing
          : labels.promotions.pageTitleCreate
      }`;
    },
  },
  data() {
    return {
      labels: labels.promotions,
      loading: false,
      submitting: false,
      type: undefined,
      form: _.cloneDeep(initialFormState),
      promotionTypes: [
        {
          label: labels.promotions.form.type.percentage_next_deposit,
          value: "percentage_next_deposit",
        },
        {
          label: labels.promotions.form.type.inmeddiate_amount,
          value: "inmeddiate_amount",
        },
        {
          label: labels.promotions.form.type.discount_next_purchase,
          value: "discount_next_purchase",
        },
      ],
      useTypes: [
        {
          label: labels.promotions.form.useType.custom_quantity,
          value: "custom_quantity",
        },
        {
          label: labels.promotions.form.useType.every_3_months,
          value: "every_3_months",
        },
        {
          label: labels.promotions.form.useType.every_6_months,
          value: "every_6_months",
        },
        {
          label: labels.promotions.form.useType.every_12_months,
          value: "every_12_months",
        },
      ],
    };
  },
  async created() {
    this.resetForm();
    if (this.$route.params.id) {
      this.fetchData();
    }
  },
  computed: {
    ...mapState("promotions", ["promotion"]),
    ...mapState("layout", ["screenWidth"]),
    ...mapGetters("adminAuth", ["getLoggedUser"]),
    isEdit() {
      return !!this.$route.params?.id;
    },
    canEdit() {
      const { accessRoles } = this.getLoggedUser;
      return (
        !this.isEdit ||
        hasRoles(accessRoles, [roles.admin.ROLE_PROMOTIONS_UPDATE])
      );
    },
    canDelete() {
      const { accessRoles } = this.getLoggedUser;
      return hasRoles(accessRoles, [roles.admin.ROLE_PROMOTIONS_DELETE]);
    },
    isPercentage() {
      return ["percentage_next_deposit", "discount_next_purchase"].includes(
        this.form?.type
      );
    },
    isPayment() {
      return this.form?.type?.name === "Pago";
    },
    dateFormat() {
      return "DD/MM/YYYY";
    },
    serverDateFormat() {
      return "YYYY-MM-DD";
    },
    money() {
      return {
        decimal: ".",
        thousands: ",",
        prefix: "",
        suffix: "",
        precision: 0,
        masked: false,
      };
    },
    isMobile() {
      return this.screenWidth < 768;
    },
  },
  methods: {
    ...mapActions("promotions", [
      "fetchPromotion",
      "createPromotion",
      "updatePromotion",
      "deletePromotion",
      "resetForm",
    ]),
    ...mapActions("users", ["fetchUsers"]),
    async onlyAlfaNumeric(value, field) {
      this.form[field] = await value
        .replace(/[^a-zA-Z0-9]/g, "")
        ?.toUpperCase();
    },
    async onlyAlfa(value = "", field) {
      this.form[field] = await value.replace(/[^a-zA-Z\s]/g, "");
    },
    async fetchData() {
      try {
        this.loading = true;
        await this.fetchPromotion(this.$route.params.id);
      } catch (error) {
        this.$message.error(
          error?.response?.data?.message ||
            error?.response?.message ||
            error.message
        );
        this.$log.error(error);
        this.$router.push({ name: "admin.promotions" });
      } finally {
        this.loading = false;
      }
    },
    async handleSubmit(closeAfterSave = false) {
      if (this.submitting) return;
      this.submitting = true;
      let response = null;
      let editting = this.isEdit;
      try {
        const formData = _.cloneDeep(this.form);
        if (formData?.start_use_at) {
          formData.start_use_at = moment(formData.start_use_at).format(
            this.serverDateFormat
          );
        }
        if (formData?.expire_at) {
          formData.expire_at = moment(formData.expire_at).format(
            this.serverDateFormat
          );
        }
        if (formData?.oatur_values) {
          formData.oatur_values = formData.oatur_values.map((item) => item.id);
        }

        if (!formData?.oatur) {
          formData.oatur_values = [];
        }

        if (!formData?.oaturwnda) {
          formData.oaturwnda_value = 0;
        }

        if (!formData?.oatuwditlqod) {
          formData.oatuwditlqod_value = 0;
        }

        response = editting
          ? await this.updatePromotion(formData)
          : await this.createPromotion(formData);

        if (response) this.$message.success(response.message);
        if (closeAfterSave) {
          this.$router.push({ name: "admin.promotions" });
        } else {
          if (!editting) {
            this.$router.push({
              name: "admin.promotions.edit",
              params: { id: response.data.id },
            });
          }
        }
      } catch (error) {
        this.$message.error(
          error?.response?.data?.message ||
            error?.response?.message ||
            error.message
        );
      } finally {
        this.submitting = false;
      }
    },
    handleCancel() {
      this.$router.push({ name: "admin.promotions" });
    },
    confirmDelete() {
      this.$confirm({
        title: this.labels.confirm.title,
        content: this.labels.confirm.subtitle,
        okText: this.labels.confirm.confirmButtonText,
        okType: "danger",
        icon: "close-circle",
        cancelText: this.labels.confirm.cancelButtonText,
        onOk: async () => {
          try {
            await this.deletePromotion(this.form.id);
            this.$store.commit(`promotions/${actions.GET_PROMOTION}`, null);
            this.$message.success(this.labels.deletedSuccess);
            this.$router.push({ name: "admin.promotions" });
          } catch (error) {
            this.$message.error(
              error?.response?.data?.message ||
                error?.response?.message ||
                error.message ||
                this.labels.deletedError
            );
          }
        },
      });
    },
  },
  watch: {
    "$route.params.id"(value, oldValue) {
      if (value && value !== oldValue) {
        this.fetchData();
      }
    },
    oatur(value, oldValue) {
      if (oldValue !== value && !value) {
        this.form.oatur_values = [];
      }
    },
    oaturwnda(value, oldValue) {
      if (oldValue !== value && !value) {
        this.form.oaturwnda_value = 0;
      }
    },
    oatuwditlqod(value, oldValue) {
      if (oldValue !== value && !value) {
        this.form.oatuwditlqod_value = 0;
      }
    },
    promotion: {
      handler(promotion) {
        Object.assign(
          this.form,
          promotion ? _.cloneDeep(promotion) : _.cloneDeep(initialFormState)
        );
      },
      deep: true,
    },
  },
  beforeDestroy() {
    this.form = _.cloneDeep(initialFormState);
    this.resetForm();
  },
};
</script>
<style lang="scss" scoped>
@import "~@/assets/scss/variable";
.promotions-form {
  .form-title {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
  }
  .form-icon {
    margin-right: 8px;
  }
  .form-button-group {
    margin-top: 16px;
  }
}
::v-deep {
  .ant-input[disabled],
  .ant-time-picker-input[disabled],
  .ant-input-number-disabled,
  .ant-select-disabled .ant-select-selection {
    background-color: color(--white) !important;
    color: rgba(0, 0, 0, 0.65) !important;
  }
}
</style>
